import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import {
  doCreatePdfAnnotationToolbarPreset,
  doDeletePdfAnnotationToolbarPreset,
  doGetPdfAnnotationToolbarPresets,
  doUpdatePdfAnnotationToolbarPreset,
  luHandleAppiError,
  type U3140PdfAnnotationToolbarPreset,
  type U3140PdfAnnotationToolbarPresetCreatePayload,
  type U3140PdfAnnotationToolbarPresetUpdatePayload,
} from "../../U3140PdfAnnotationsService";

export interface U3140UsePdfAnnotationToolbarPresetHandlersParams {
  i18nNamespace: string;
  doGetPdfAnnotationToolbarPresetsFn?: () => Promise<U3140PdfAnnotationToolbarPreset[]>;
  doCreatePdfAnnotationToolbarPresetFn?: (payload: U3140PdfAnnotationToolbarPresetCreatePayload) => Promise<unknown>;
  doUpdatePdfAnnotationToolbarPresetFn?: (
    presetId: string | number,
    payload: U3140PdfAnnotationToolbarPresetUpdatePayload
  ) => Promise<unknown>;
  doDeletePdfAnnotationToolbarPresetFn?: (presetId: string | number) => Promise<unknown>;
  onError?: (error: unknown) => void;
}

export function useU3140UsePdfAnnotationToolbarPresetHandlers(params: U3140UsePdfAnnotationToolbarPresetHandlersParams) {
  const axios = useU2000AxiosAuthenticated();
  const { i18nNamespace } = params;
  const { t } = useTranslation(i18nNamespace);
  const toast = useToast();
  const doGetOverride = params.doGetPdfAnnotationToolbarPresetsFn;
  const doCreateOverride = params.doCreatePdfAnnotationToolbarPresetFn;
  const doUpdateOverride = params.doUpdatePdfAnnotationToolbarPresetFn;
  const doDeleteOverride = params.doDeletePdfAnnotationToolbarPresetFn;
  const onError = params.onError;

  const loadPresets = useCallback(async () => {
    try {
      const fn = doGetOverride ?? (() => doGetPdfAnnotationToolbarPresets(axios));
      return await fn();
    } catch (error: unknown) {
      onError?.(error);
      const err = U2000HandleApiError(
        error as Parameters<typeof U2000HandleApiError>[0],
        t,
        i18nNamespace,
        luHandleAppiError
      );
      if (err?.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
      throw error;
    }
  }, [axios, doGetOverride, i18nNamespace, onError, t, toast]);

  const createPreset = useCallback(
    async (payload: U3140PdfAnnotationToolbarPresetCreatePayload) => {
      try {
        const fn =
          doCreateOverride ??
          ((p: U3140PdfAnnotationToolbarPresetCreatePayload) => doCreatePdfAnnotationToolbarPreset(axios, p));
        await fn(payload);
      } catch (error: unknown) {
        onError?.(error);
        const err = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          i18nNamespace,
          luHandleAppiError
        );
        if (err?.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        throw error;
      }
    },
    [axios, doCreateOverride, i18nNamespace, onError, t, toast]
  );

  const updatePreset = useCallback(
    async (presetId: string | number, payload: U3140PdfAnnotationToolbarPresetUpdatePayload) => {
      try {
        const fn =
          doUpdateOverride ??
          ((id: string | number, p: U3140PdfAnnotationToolbarPresetUpdatePayload) =>
            doUpdatePdfAnnotationToolbarPreset(axios, id, p));
        await fn(presetId, payload);
      } catch (error: unknown) {
        onError?.(error);
        const err = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          i18nNamespace,
          luHandleAppiError
        );
        if (err?.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        throw error;
      }
    },
    [axios, doUpdateOverride, i18nNamespace, onError, t, toast]
  );

  const deletePreset = useCallback(
    async (presetId: string | number) => {
      try {
        const fn = doDeleteOverride ?? ((id: string | number) => doDeletePdfAnnotationToolbarPreset(axios, id));
        await fn(presetId);
      } catch (error: unknown) {
        onError?.(error);
        const err = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          i18nNamespace,
          luHandleAppiError
        );
        if (err?.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        throw error;
      }
    },
    [axios, doDeleteOverride, i18nNamespace, onError, t, toast]
  );

  return { loadPresets, createPreset, updatePreset, deletePreset };
}
